import PropTypes from "prop-types";
import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Chip, Tooltip, Typography } from "@material-ui/core";
import { People as PeopleIcon } from "@material-ui/icons";
import styled from "styled-components";
import { groupAtaParticipants } from "./ataDraftStore";
import { theme as appTheme } from "../../assets/theme/StyledTheme";

const TipRoot = styled.div`
  max-width: 280px;
  padding: 4px 2px;
`;

const TipGroup = styled.div`
  & + & {
    margin-top: 10px;
  }
`;

const TipGroupTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
  margin-bottom: 4px;
`;

const TipRow = styled.div`
  font-size: 13px;
  line-height: 1.35;
  margin-bottom: 3px;

  .meta {
    display: block;
    font-size: 11px;
    opacity: 0.75;
  }
`;

const ChipWrap = styled.span`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  cursor: default;
`;

function ParticipantsTooltipBody({ groups, t }) {
  const schoolLabel =
    t("smartMinutes.action.schoolAudience") || "Escola";
  const familyLabel = t("smartMinutes.action.family") || "Família";
  const empty = t("smartMinutes.participants.emptyHover");

  if (!groups.total) {
    return <TipRoot>{empty}</TipRoot>;
  }

  return (
    <TipRoot>
      {groups.family.length > 0 && (
        <TipGroup>
          <TipGroupTitle>{familyLabel}</TipGroupTitle>
          {groups.family.map(p => (
            <TipRow key={p.id}>
              {p.name}
              {p.meta ? <span className="meta">{p.meta}</span> : null}
            </TipRow>
          ))}
        </TipGroup>
      )}
      {groups.school.length > 0 && (
        <TipGroup>
          <TipGroupTitle>{schoolLabel}</TipGroupTitle>
          {groups.school.map(p => (
            <TipRow key={p.id}>
              {p.name}
              {p.meta ? <span className="meta">{p.meta}</span> : null}
            </TipRow>
          ))}
        </TipGroup>
      )}
    </TipRoot>
  );
}

ParticipantsTooltipBody.propTypes = {
  groups: PropTypes.shape({
    total: PropTypes.number,
    family: PropTypes.arrayOf(PropTypes.shape({})),
    school: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  t: PropTypes.func.isRequired
};

/**
 * Ícone + total de participantes; hover mostra Família × Escola.
 */
export default function AtaParticipantsChip({ ata, size }) {
  const { t } = useTranslation();
  const groups = groupAtaParticipants(ata, t);
  const label =
    groups.total > 0
      ? t("smartMinutes.participants.count", { count: groups.total })
      : t("smartMinutes.participants.none");

  return (
    <Tooltip
      interactive
      placement="bottom-start"
      enterDelay={200}
      leaveDelay={150}
      title={<ParticipantsTooltipBody groups={groups} t={t} />}
    >
      <ChipWrap
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
        role="presentation"
      >
        <Chip
          size={size}
          icon={<PeopleIcon />}
          label={(
            <Box component="span" display="inline-flex" alignItems="center">
              <Typography
                component="span"
                variant="body2"
                style={{ fontWeight: 600, fontSize: size === "small" ? 12 : 13 }}
              >
                {groups.total}
              </Typography>
            </Box>
          )}
          style={{
            background: appTheme.color.primary.light,
            color: appTheme.color.primary.dark,
            height: size === "small" ? 26 : 30,
            cursor: "default"
          }}
          title={label}
          aria-label={label}
        />
      </ChipWrap>
    </Tooltip>
  );
}

AtaParticipantsChip.propTypes = {
  ata: PropTypes.shape({
    students: PropTypes.arrayOf(PropTypes.shape({})),
    staff: PropTypes.arrayOf(PropTypes.shape({}))
  }),
  size: PropTypes.oneOf(["small", "medium"])
};

AtaParticipantsChip.defaultProps = {
  ata: null,
  size: "small"
};
